import { Button, confirm, Modal, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  DownloadIcon,
  EditIcon,
  LinkIcon,
  TrashIcon,
} from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { SOPDetailDTO } from "@/modules/sop/domain/contracts";
import type { CreateSOPMappingDTO } from "@/modules/sop/domain/contracts/mapping";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import {
  useCreateSOPMapping,
  useDeleteSOPMapping,
  useSOPDetailQuery,
  useSOPMappingsBySopId,
  useSOPSUsage,
} from "../api/queries";
import { SOPDetailContent } from "../ui/SOPDetailContent";
import { SOPMappingForm } from "../ui/SOPMappingForm";
import { SOPUsageView } from "../ui/SOPUsageView";

export function SOPDetailPage() {
  const { id } = useParams({ from: "/app/sop/$id" });
  const nav = useNavigate({ from: "/app/sop/$id" });
  const canEdit = useActionPermission(["sop:update"]);
  const canCreateMapping = useActionPermission(["sop:create"]);

  const { data: sop, isLoading, error } = useSOPDetailQuery(id);
  const { refetch: refetchUsage } = useSOPSUsage(id);
  const { data: mappingsData, refetch: refetchMappings } =
    useSOPMappingsBySopId(id);
  const createMapping = useCreateSOPMapping();
  const deleteMapping = useDeleteSOPMapping();
  const canDeleteMapping = useActionPermission(["sop:delete"]);

  const {
    isOpen: isMappingModalOpen,
    open: onMappingModalOpen,
    close: onMappingModalClose,
  } = useDisclosure();

  const handleDownloadPDF = () => {
    nav({ to: "/app/sop/$id/pdf-preview", params: { id } });
  };

  const handleEdit = () => {
    nav({ to: "/app/sop/$id/edit", params: { id } });
  };

  const handleBack = () => {
    nav({ to: "/app/sop" });
  };

  const handleCreateMapping = (
    values:
      | CreateSOPMappingDTO
      | { sopId?: string; positionId?: string; departmentId?: string | null },
  ) => {
    const mappingData: CreateSOPMappingDTO = {
      sopId: id,
      positionId: values.positionId || "",
      departmentId: values.departmentId || undefined,
    };
    createMapping.mutate(mappingData, {
      onSuccess: () => {
        onMappingModalClose();
        refetchUsage();
        refetchMappings();
      },
    });
  };

  const handleDeleteMapping = async (mappingId: string) => {
    const ok = await confirm({
      title: "ລຶບການຜູກ SOP",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບການຜູກນີ້?",
      actionText: "ລຶບ",
      ActionProps: {
        variant: "destructive",
      },
    });
    if (ok) {
      await toast.promise(deleteMapping.run(mappingId), {
        loading: "ກຳລັງລຶບ...",
        success: "ລຶບການຜູກ SOP ສຳເລັດ",
        error: "ລຶບການຜູກ SOP ລົ້ມເຫຼວ",
      });
      refetchUsage();
      refetchMappings();
    }
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
              <p className="mt-2 text-muted-foreground">ກໍາລັງໂຫຼດ...</p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (error || !sop || !sop.id) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <h2 className="mb-2 font-bold text-2xl text-destructive">
                ບໍ່ພົບ SOP
              </h2>
              <p className="mb-4 text-muted-foreground">
                SOP ທີ່ທ່ານຊອກຫາບໍ່ມີຢູ່ ຫຼື ຖືກລຶບໄປແລ້ວ
              </p>
              <Button onClick={handleBack}>
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                ກັບໄປທີ່ລາຍການ SOP
              </Button>
            </div>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />

      <Main>
        {/* Header Actions */}
        <div className="mb-6 flex items-center justify-between">
          <Button variant="ghost" onClick={handleBack} className="gap-2">
            <ArrowLeftIcon className="h-4 w-4" />
            ກັບໄປທີ່ລາຍການ SOP
          </Button>

          <div className="flex items-center gap-2">
            {canCreateMapping && (
              <Button
                variant="outline"
                onClick={onMappingModalOpen}
                className="gap-2"
              >
                <LinkIcon className="h-4 w-4" />
                ຜູກກັບຕໍາແໜ່ງ
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleDownloadPDF}
              className="gap-2"
            >
              <DownloadIcon className="h-4 w-4" />
              ເບິ່ງ PDF Preview
            </Button>
            {canEdit && (
              <Button onClick={handleEdit} className="gap-2">
                <EditIcon className="h-4 w-4" />
                ແກ້ໄຂ
              </Button>
            )}
          </div>
        </div>

        {/* SOP Content */}
        <SOPDetailContent sop={sop as unknown as SOPDetailDTO} />

        {/* SOP Usage */}
        <div className="mt-6 rounded-xl border bg-card p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-semibold text-xl">ການນຳໃຊ້ SOP ນີ້</h2>
            {canCreateMapping && (
              <Button
                variant="outline"
                size="sm"
                onClick={onMappingModalOpen}
                className="gap-2"
              >
                <LinkIcon className="h-4 w-4" />
                ຜູກກັບຕໍາແໜ່ງ
              </Button>
            )}
          </div>
          <SOPUsageView sopId={id} />

          {/* Mappings List with Delete */}
          {mappingsData?.data && mappingsData.data.length > 0 && (
            <div className="mt-6 space-y-2">
              <h3 className="font-semibold text-lg">ລາຍການການຜູກ</h3>
              <div className="space-y-2">
                {mappingsData.data.map((mapping) => (
                  <div
                    key={mapping.id}
                    className="flex items-center justify-between rounded-lg border p-3"
                  >
                    <div className="flex-1">
                      <div className="font-medium">
                        {mapping.positionTitle || "ບໍ່ມີຕໍາແໜ່ງ"}
                      </div>
                      {mapping.departmentName && (
                        <div className="text-muted-foreground text-sm">
                          {mapping.departmentName}
                        </div>
                      )}
                    </div>
                    {canDeleteMapping && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteMapping(mapping.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Mapping Modal */}
        <Modal
          open={isMappingModalOpen}
          onOpenChange={(open) => {
            if (!open) onMappingModalClose();
          }}
          title="ຜູກ SOP ກັບຕໍາແໜ່ງ"
        >
          <SOPMappingForm
            initialValues={{ sopId: id, positionId: "", departmentId: "" }}
            onSubmit={(values) => {
              handleCreateMapping(values);
            }}
            submitting={createMapping.isPending}
          />
        </Modal>
      </Main>
    </>
  );
}
